/**
 * Result of attempting to coerce an arbitrary DynamoDB attribute value
 * (string, map, list, number, boolean, null) into a JSON-serializable
 * value plus a "raw" string representation suitable for display/search.
 */
export interface ParsedJsonField {
  raw: string;
  parsed: unknown;
  malformed: boolean;
}

function stableStringify(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * DynamoDB `input`/`response` attributes may already have been stored as
 * native maps/lists (if written via the DynamoDB Document API) or as JSON
 * strings (if written by a client that pre-serialized the payload). This
 * function normalizes either shape into a `{ raw, parsed, malformed }`
 * triple without ever throwing.
 */
export function parseJsonField(value: unknown): ParsedJsonField {
  if (value === null || value === undefined) {
    return { raw: "", parsed: null, malformed: false };
  }

  if (typeof value === "object") {
    return { raw: stableStringify(value), parsed: value, malformed: false };
  }

  if (typeof value === "boolean" || typeof value === "number") {
    return { raw: String(value), parsed: value, malformed: false };
  }

  if (typeof value !== "string") {
    return { raw: String(value), parsed: null, malformed: true };
  }

  const trimmed = value.trim();
  if (trimmed === "") {
    return { raw: value, parsed: null, malformed: false };
  }

  try {
    const parsed: unknown = JSON.parse(trimmed);
    return { raw: value, parsed, malformed: false };
  } catch {
    return { raw: value, parsed: null, malformed: true };
  }
}

export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  );
}

export function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
